/**
 * invoiceTotals - integer-paise money helpers for the ABDM Invoice form (ABDM-M2-HITYPES INV-F0).
 *
 * Every amount here is an INTEGER number of paise. Typed rupees are parsed from the string by
 * digits (never `parseFloat(x) * 100`, which turns "1.15" into 114.99999999999999), and totals are
 * integer sums - no float arithmetic anywhere. Display formatting (lakh/crore grouping) is the
 * app's one formatter, `formatPaise` in utils/money.js; this file only builds the numbers.
 */

// A typed ₹ amount: digits, optional thousands commas (Western 1,240,500 or Indian 12,40,500 -
// commas anywhere else are garbage), optional 1-2 decimals. Group 1 = rupees, group 2 = decimals.
// No sign, no exponent, no currency symbol, no spaces inside. ".5" is accepted (= 50 paise).
const RUPEES = /^(\d+|\d{1,3}(?:,\d{3})+|\d{1,2}(?:,\d{2})*,\d{3})?(?:\.(\d{1,2}))?$/;

/**
 * Typed rupees -> integer paise, exactly. "1,240.50" -> 124050, "0.7" -> 70, "5" -> 500.
 * Returns null for anything that is not a plain non-negative amount of at most 2 decimals:
 * blank, negative, "1.234", "abc", "1e3", "₹5", a number (not a string), or a value too big to
 * hold exactly. null = "not an amount": the caller shows its inline error and sends nothing.
 *
 * @param {string} text what the user typed
 * @returns {number|null}
 */
export function parseRupeesToPaise(text) {
  if (typeof text !== 'string') return null;
  const m = RUPEES.exec(text.trim());
  if (!m || (m[1] === undefined && m[2] === undefined)) return null; // blank, or only a "."
  const rupees = m[1] === undefined ? 0 : Number(m[1].replace(/,/g, ''));
  const paise = rupees * 100 + (m[2] === undefined ? 0 : Number(m[2].padEnd(2, '0')));
  return Number.isSafeInteger(paise) ? paise : null;
}

/**
 * Integer paise -> the plain editable rupee string that `parseRupeesToPaise` reads back: 124050 ->
 * "1240.50", 124000 -> "1240", 7 -> "0.07". No grouping, no ₹ (that is `formatPaise`'s job).
 * For a prefilled / saved amount going into an input. `paise` must be a non-negative integer.
 *
 * @param {number} paise
 * @returns {string}
 */
export function paiseToRupeeInput(paise) {
  const rem = paise % 100;
  const rupees = (paise - rem) / 100; // exact: a multiple of 100 divided by 100
  return rem === 0 ? String(rupees) : `${rupees}.${String(rem).padStart(2, '0')}`;
}

const isAmount = (v) => Number.isSafeInteger(v) && v >= 0;

/**
 * Live Net / Gross of an invoice (OR-24) - the SAME formula as the server's `compute_totals`
 * (INV-B1) and the same cases as its fixture table, so the form's figures equal what the server
 * stores:
 *   totalNet   = sum(rate) - sum(discount)            (before tax)
 *   totalGross = totalNet + sum(cgst + sgst)
 * Pure arithmetic over the lines as given. It takes no invoice type: CGST/SGST are always added,
 * because "GST on Pharmacy only" (OR-22) is a validation rule, not a formula - the form shows the
 * GST columns for Pharmacy only (and clears them, after a confirm, when the type moves away) and
 * the server answers GST_NOT_ALLOWED to GST on any other type. Net can come out negative when the
 * discounts exceed the rates; that is flagged by validation (DISCOUNT_EXCEEDS_RATE), not hidden here.
 *
 * Returns null - never a rounded or partial figure - when any amount is not a non-negative integer
 * of paise (a float, a string, NaN, a blank/unparsed field, negative) or the sum would leave the
 * exactly-representable range. The server would refuse such lines, so there is nothing to total.
 *
 * @param {Array<{ ratePaise: number, discountPaise: number, cgstPaise: number, sgstPaise: number }>} lines
 * @returns {{ totalNetPaise: number, totalGrossPaise: number }|null}
 */
export function computeInvoiceTotals(lines) {
  let net = 0;
  let tax = 0;
  for (const line of lines) {
    if (!isAmount(line.ratePaise) || !isAmount(line.discountPaise)
      || !isAmount(line.cgstPaise) || !isAmount(line.sgstPaise)) return null;
    net += line.ratePaise - line.discountPaise;
    tax += line.cgstPaise + line.sgstPaise;
  }
  const gross = net + tax;
  if (!Number.isSafeInteger(net) || !Number.isSafeInteger(tax) || !Number.isSafeInteger(gross)) return null;
  return { totalNetPaise: net, totalGrossPaise: gross };
}
